import React, { useId, useMemo, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import "../../../css/DesignSystem/Inputs/MobileNumberInput.css";
import "react-phone-number-input/style.css";

const supportedCountries = [
  "GB",
  "IE",
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "US",
  "CA",
  "AR",
  "BR",
  "CL",
  "CO",
  "MX",
  "PE",
  "UY",
  "VE",
  "IN",
  "CN",
  "JP",
  "KR",
  "SG",
  "HK",
  "MY",
  "TH",
  "VN",
  "PH",
  "ID",
  "AU",
  "NZ",
  "AE",
  "SA",
  "QA",
  "KW",
  "BH",
  "OM",
  "ZA",
  "NG",
  "KE",
  "EG",
];

function normalisePhone(raw, defaultCountry = "GB") {
  const v = String(raw ?? "").trim();
  if (!v) return "";

  const cleaned = v.replace(/^00/, "+").replace(/\s+/g, " ");
  const phone = parsePhoneNumberFromString(cleaned, defaultCountry);
  return phone?.number || ""; // E.164
}

const MobileNumberInput = ({
  id,
  label = "",
  placeholder = "Mobile number",
  value,
  onChange,
  isRequired = false,
  disabled = false,
  defaultCountry = "GB",
  error = "",
}) => {
  const autoId = useId();
  const inputId = id || `mobile-${autoId}`;
  const [touched, setTouched] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  // normalise whatever you receive (even if it already includes + / country code)
  const normalisedValue = useMemo(
    () => normalisePhone(value, defaultCountry),
    [value, defaultCountry],
  );

  // Convert empty string to undefined so placeholder shows
  const phoneValue = normalisedValue ? normalisedValue : undefined;

  const invalid = touched && phoneValue && !isValidPhoneNumber(phoneValue);

  return (
    <div
      className={`AnnouncementInputField ${invalid || error ? "AnnouncementInputField--error" : ""}`}
    >
      {label && (
        <div
          htmlFor={inputId}
          className="FP_Content text-black"
          aria-live="polite"
        >
          {label}
          {isRequired && (
            <span className="FP_Content_Semibold text-error">*</span>
          )}
        </div>
      )}

      <div
        className={`ds-input-wrapper ds-phone-input ${hasFocus ? "is-focused" : ""}`}
        onFocusCapture={() => setHasFocus(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setHasFocus(false);
            setTouched(true);
          }
        }}
      >
        <PhoneInput
          id={inputId}
          value={phoneValue}
          onChange={(val) => {
            // val is usually E.164 already, but normalise anyway (safe)
            const next = normalisePhone(val || "", defaultCountry);
            onChange?.(next);
          }}
          placeholder={placeholder}
          defaultCountry={defaultCountry}
          countries={supportedCountries}
          international
          withCountryCallingCode
          countryCallingCodeEditable={false}
          limitMaxLength
          disabled={disabled}
          aria-invalid={Boolean(invalid || error)}
          aria-label={label ? undefined : placeholder}
          className="Content ds-phone-input-control"
        />
      </div>
    </div>
  );
};

export default MobileNumberInput;
